import React, { useCallback, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  TouchSensor,
  MouseSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortableEntity, { RankingEntity, Team } from '../../SortableEntity';
import TieConnector from '../../TieConnector';
import { useRankingLogic } from '../../../hooks/useRankingLogic';
import { Player, PlayerWithLeagueContext } from '../../../generated-api';

interface RankingStepProps {
  rankingEntities: RankingEntity[];
  onRankingEntitiesChange: (updater: (prev: RankingEntity[]) => RankingEntity[]) => void;
  teams: Team[];
  players: (Player | PlayerWithLeagueContext)[];
}

const RankingStep: React.FC<RankingStepProps> = ({
  rankingEntities,
  onRankingEntitiesChange,
  teams,
  players,
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const {
    draggedOverId,
    handleDragStart: baseHandleDragStart,
    handleDragOver,
    handleDragEnd: baseHandleDragEnd,
    createTie,
    separateFromTie,
  } = useRankingLogic();

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback(
    (event: Parameters<typeof baseHandleDragStart>[0]) => {
      setIsDragging(true);
      baseHandleDragStart(event);
    },
    [baseHandleDragStart]
  );

  const handleDragEndWrapper = useCallback(
    (event: Parameters<typeof baseHandleDragEnd>[0]) => {
      setIsDragging(false);
      baseHandleDragEnd(event, onRankingEntitiesChange);
    },
    [baseHandleDragEnd, onRankingEntitiesChange]
  );

  const createTieWrapper = useCallback(
    (entityId1: string, entityId2: string) => {
      createTie(entityId1, entityId2, onRankingEntitiesChange);
    },
    [createTie, onRankingEntitiesChange]
  );

  const separateFromTieWrapper = useCallback(
    (entityId: string) => {
      separateFromTie(entityId, onRankingEntitiesChange);
    },
    [separateFromTie, onRankingEntitiesChange]
  );

  const sortedEntities = [...rankingEntities].sort((a, b) => a.position - b.position);

  return (
    <div className="flex flex-col gap-3">
      {/* Drag-drop ranking */}
      <div>
        <h3 className="text-xs font-medium text-muted uppercase tracking-wide mb-2">Final Ranking</h3>
        <p className="text-sm text-body mb-3">
          Drag to reorder. Tap chain to tie.
        </p>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEndWrapper}
        >
          <SortableContext
            items={rankingEntities.map(e => e.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-0">
              {sortedEntities.map((entity, index) => (
                <React.Fragment key={entity.id}>
                  <SortableEntity
                    entity={entity}
                    teams={teams}
                    allPlayers={players}
                    allEntities={rankingEntities}
                    onSeparateFromTie={separateFromTieWrapper}
                    onCreateTie={createTieWrapper}
                    isDraggedOver={draggedOverId === entity.id}
                  />
                  {index < sortedEntities.length - 1 && (
                    <TieConnector
                      entityAbove={entity}
                      entityBelow={sortedEntities[index + 1]}
                      onCreateTie={createTieWrapper}
                      onSeparateFromTie={separateFromTieWrapper}
                      isDragging={isDragging}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default RankingStep;
